import { useEffect, useState } from 'react';
import { Card } from '@safetyhub/ui';
import { adminApi, extractApiError, VersionInfo } from '../lib/api';

interface VersionCardProps {
  version: VersionInfo | null;
  loading?: boolean;
}

function VersionCard({ version, loading }: VersionCardProps) {
  if (loading) {
    return (
      <Card elevation="none" className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-24 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </Card>
    );
  }

  if (!version) {
    return (
      <Card elevation="none">
        <div className="text-sm text-gray-500">-</div>
        <div className="text-xl font-semibold text-gray-300">N/A</div>
      </Card>
    );
  }

  // 날짜/시간 포맷: 2026. 1. 19. 오후 3:30
  const formatDateTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
        // 날짜만 있는 경우 (예: 2026-01-19)
        return isoString
          .split('-')
          .map((part, i) => (i === 0 ? part : parseInt(part, 10)))
          .join('. ') + '.';
      }
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return isoString;
    }
  };

  const formattedDate = formatDateTime(version.buildDate);

  return (
    <Card elevation="none" className="hover:shadow-md transition-shadow">
      <div className="text-sm text-gray-500 capitalize">{version.name}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">
        {version.fullVersion}
      </div>
      <div className="text-sm text-gray-400 mt-2">{formattedDate}</div>
      <div className="text-sm text-gray-400 font-mono">{version.commitHash}</div>
    </Card>
  );
}

export function SystemInfoCard() {
  const [systemInfo, setSystemInfo] = useState<{
    server: VersionInfo | null;
    web: VersionInfo | null;
    mobile: VersionInfo | null;
    admin: VersionInfo | null;
  } | null>(null);
  const [adminVersion, setAdminVersion] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin 앱 자체 버전 (version.json에서 직접 fetch)
  useEffect(() => {
    async function fetchAdminVersion() {
      try {
        setAdminLoading(true);
        const response = await fetch('/version.json');
        if (response.ok) {
          const data = await response.json();
          setAdminVersion(data);
        }
      } catch {
        // 실패 시 무시 (개발 환경에서는 version.json이 없을 수 있음)
      } finally {
        setAdminLoading(false);
      }
    }
    fetchAdminVersion();
  }, []);

  useEffect(() => {
    async function fetchSystemInfo() {
      try {
        setLoading(true);
        const response = await adminApi.getSystemInfo();

        if (response.success && response.data) {
          setSystemInfo(response.data);
        } else {
          throw new Error(response.error?.message || '알 수 없는 오류');
        }
      } catch (err) {
        const apiError = extractApiError(err);
        setError(apiError.message);
      } finally {
        setLoading(false);
      }
    }

    fetchSystemInfo();
  }, []);

  if (error) {
    return (
      <Card padding="xl" radius="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">시스템 정보</h2>
        <div className="text-red-500">{error}</div>
      </Card>
    );
  }

  return (
    <Card padding="xl" radius="lg">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">시스템 정보</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VersionCard version={adminVersion} loading={adminLoading} />
        <VersionCard version={systemInfo?.server || null} loading={loading} />
        <VersionCard version={systemInfo?.web || null} loading={loading} />
        <VersionCard version={systemInfo?.mobile || null} loading={loading} />
      </div>
    </Card>
  );
}
